import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import {
    AbstractControl,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { select, Store } from '@ngrx/store';
import { Select2Option } from 'ng-select2-component';
import { combineLatest, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ActionService } from 'src/app/core/services/action.service';
import { RouteService } from 'src/app/core/services/route.service';
import {
    CreateField,
    LoadFields,
    LoadFieldTypes,
    UpdateField,
} from 'src/app/core/state/_actions/field.actions';
import { Field, MethodParamData } from 'src/app/core/state/_models/field.model';
import { OpTypes, Select2Data } from 'src/app/core/state/_models/general.model';
import {
    getFieldOperations,
    getFields,
    getFieldTypes,
    getFieldTypesLoading,
} from 'src/app/core/state/_selectors/field.selector';
import {
    AlertService,
    ToastTypes,
} from 'src/app/shared/services/alert.service';
import {
    allowedHtmlTags,
    getOriginalFunctions,
    methodsWithParam,
} from './upsert.constants';

import { LoadApplicationRoles } from 'src/app/core/state/_actions/user.actions';
import {
    getApplicationRoles,
    getApplicationRolesLoading,
} from 'src/app/core/state/_selectors/user.selector';
import { SubSink } from 'subsink';
import { SubmitGuard } from 'src/app/shared/helpers/submit-guard';
import { LaunchDarklyService } from 'src/app/core/services/launchdarkly.service';
import { FEATURE_FLAGS } from 'src/app/shared/launchdarkly.constants';

@Component({
    selector: 'app-field-upsert',
    templateUrl: './upsert.component.html',
    styleUrls: ['./upsert.component.scss'],
})
export class UpsertFieldComponent implements OnInit {
    @Input('key') key: string = '';
    @Input('fieldName') fieldName: string = '';
    form: UntypedFormGroup = new UntypedFormGroup({});
    private readonly submitGuard = new SubmitGuard();
    fieldTypes: Array<Select2Data> | null = null;
    fields: Array<Select2Data> | null = null;
    fieldOptions: Array<Select2Data> | null = null;
    filterdOptions: any = [];
    selectedFieldObject: Select2Option = <Select2Option>{};
    editor: any;
    syntaxErrors: boolean = false;
    fieldErrors: boolean = false;
    functionErrors: boolean = false;
    htmlTagErrors: boolean = false;
    operatorErrors: boolean = false;
    invalidSyntax: boolean = false;
    originalFunctionNames: Array<any> = [];
    currentOperatorNames = new Array<[string, number, number, number]>();
    currentNotEqualExpressions = new Array<[string, number, number, number]>();
    oldFieldDecorations: Array<any> = [];
    oldFunctionDecorations: Array<any> = [];
    oldHtmlTagDecorations: Array<any> = [];
    oldOperatorDecorations: Array<any> = [];
    OperatorErrors: Array<any> = [];
    oldJavaScriptsRanges: Array<any> = [];
    oldJavaScriptDecorations: Array<any> = [];
    isHtml: boolean = false;
    urlFieldNameLabelHelper: string =
        'Use "View Record" to display link in Summary Report';
    matchMessage: string = '';
    editorOptions: any = {
        theme: 'vs',
        language: 'javascript',
        automaticLayout: true,
        formatOnType: true,
        formatOnPaste: false,
        glyphMargin: true,
        minimap: { enabled: false },
        definitionLinkOpensInPeek: true,
        suggest: {
            showKeywords: false,
            showVariables: false,
            showModules: false,
        },
        scrollbar: {
            // Subtle shadows to the left & top. Defaults to true.
            useShadows: false,

            // Render vertical arrows. Defaults to false.
            // verticalHasArrows: true,
            // Render horizontal arrows. Defaults to false.
            // horizontalHasArrows: true,

            // Render vertical scrollbar.
            // Accepted values: 'auto', 'visible', 'hidden'.
            // Defaults to 'auto'
            vertical: 'visible',
            // Render horizontal scrollbar.
            // Accepted values: 'auto', 'visible', 'hidden'.
            // Defaults to 'auto'
            horizontal: 'visible',

            verticalScrollbarSize: 12,
            horizontalScrollbarSize: 12,
            quickSuggestions: { other: true, strings: true },
            // arrowSize: 30
        },
    };
    iDisposable: any;
    noMarkersCount = 0;
    groupSummaryType: Array<Select2Data> = [
        {
            label: 'Sum',
            value: 'sum',
        },
        {
            label: 'Avg',
            value: 'avg',
        },
    ];
    relationFieldType: string | undefined;
    isUnique: boolean = false;
    isNullable: boolean = false;
    isExternalUrl: boolean = false;
    isFile: boolean = false;
    applicationRoles: Array<Select2Data> | null = null;
    isUpsertFieldComponent: boolean = true;
    subsink: SubSink = new SubSink();
    tableKey: string = '';
    applicationKey: number = 0;
    isApplicationRolesLoading: { [key: string]: boolean } = {};
    isFieldTypesLoading: { [key: string]: boolean } = {};
    private subscriptions: Subscription[] = []; // Array to hold all subscriptions
    isFormulaChanged: boolean = false;
    isUuidEnabled: boolean = false;

    constructor(
        private fb: UntypedFormBuilder,
        private store: Store,
        private modal: NgbModal,
        private route: RouteService,
        private action: ActionService,
        private alert: AlertService,
        private readonly cd: ChangeDetectorRef,
        private readonly ldService: LaunchDarklyService
    ) { }

    ngOnInit(): void {
        this.applicationKey = this.route.getApplicationKey();
        this.subsink.add(
            this.ldService.getFlagValue(FEATURE_FLAGS.ENABLE_UUID_FIELD_TYPE, false)
                .subscribe((val: any) => this.isUuidEnabled = !!val)
        );
        this.getApiState();
        this.initForm();
        this.loadSubscribers();
        this.store.dispatch(
            new LoadApplicationRoles(this.route.getApplicationKey())
        );
        this.store.dispatch(new LoadFieldTypes());
    }

    getApiState() {
        const combinedSubscription = combineLatest([
            this.store.pipe(select(getApplicationRolesLoading)),
            this.store.pipe(select(getFieldTypesLoading)),
        ]).subscribe(([isApplicationRolesLoading, isFieldTypesLoading]) => {
            this.isApplicationRolesLoading = isApplicationRolesLoading;
            this.isFieldTypesLoading = isFieldTypesLoading;
        });

        this.subsink.add(combinedSubscription);
    }

    ngOnDestroy(): void {
        if (this.iDisposable) {
            this.iDisposable.dispose();
        }
        this.subsink.unsubscribe();
    }

    /**
     * Init form
     */
    initForm = () => {
        this.form = this.fb.group({
            Name: [
                null,
                [
                    Validators.required,
                    Validators.minLength(3),
                    Validators.maxLength(64),
                    this.customPatternValidator,
                ],
            ],
            LookupDisplayName: [null],
            Info: [null],
            FieldType: [null, Validators.required],
            ApplicationTableKey: this.route.getTableKey(),
            IsUnique: [false],
            IsNullable: [false],
            IsExternalUrl: [false],
            IsHtmlAllowed: [false],
            IsHtml: [false],
            IsFlyAdditionAllowed: [false],
            IsAlphaSort: [false],
            IsRelation: [false],
            isGroupSummaryType: [false],
            onGroupingSummaryType: [null],
            Values: new UntypedFormArray([]),
            Formula: [''],
            EvalFormula: [null],
            IsFormula: [false],
            FormulaFields: [null],
            FieldSize: [100, [Validators.min(1), Validators.max(255)]],
            Key: [null],
            PIILength: [null],
            PIIDirection: [null],
            LinkText: [null],
            EnableDoc: [false],
            AlwaysExecuteOnCreateRecord: [false],
            FieldRoles: [null],
            LookupTargetFieldKey: [null],
        });
        if (this.fieldName) {
            this.form.patchValue({
                Name: this.fieldName,
            });
        }

        this.subsink.add(
        this.form.controls.FieldType.valueChanges.subscribe((type: any) => {
            if (
                type === 'Encrypted (Standard)' ||
                type === 'Encrypted (Extra Secure)'
            ) {
                this.form.controls.PIILength.addValidators([
                    Validators.required,
                    Validators.min(1),
                ]);
                this.form.controls.PIIDirection.addValidators([
                    Validators.required,
                ]);
                this.form.controls.PIILength.updateValueAndValidity();
                this.form.controls.PIIDirection.updateValueAndValidity();
                if (type === 'Encrypted (Standard)') {
                    this.form.controls.FieldRoles.addValidators([
                        Validators.required,
                    ]);
                    this.form.controls.FieldRoles.updateValueAndValidity();
                }
            } else {
                this.form.controls.PIILength.clearValidators();
                this.form.controls.PIIDirection.clearValidators();
                this.form.controls.PIILength.updateValueAndValidity();
                this.form.controls.PIIDirection.updateValueAndValidity();
                this.form.controls.FieldRoles.clearValidators();
                this.form.controls.FieldRoles.updateValueAndValidity();
            }

            this.subsink.add(
            this.form.controls.isGroupSummaryType.valueChanges.subscribe(
                (value) => {
                    if (value) {
                        this.form.controls.onGroupingSummaryType.addValidators(
                            Validators.required
                        );
                        this.form.controls.onGroupingSummaryType.updateValueAndValidity();
                    } else {
                        this.form.controls.onGroupingSummaryType.clearValidators();
                        this.form.controls.onGroupingSummaryType.updateValueAndValidity();
                    }
                }
            ));
            this.cd.detectChanges();
        }));
    };

    /**
     * @param editor
     */
    editorInit(editor: any) {
        // Programatic content selection example
        this.editor = editor;
        let fullRange = this.editor.getModel().getFullModelRange();

        let data = [
            'If ([createdby] = "Today", "Yes",' +
            '\r\tIf([createdby] = "Tomorrow", "First", "Second"))',
        ].join('\n');

        if (this.toBeUpdated()) {
            data = this.form.controls.Formula.value;
        }

        // Apply the text over the range
        this.editor.executeEdits(null, [
            {
                text: data,
                range: fullRange,
            },
        ]);
        this.iDisposable = (
            window as any
        )?.monaco?.languages?.registerCompletionItemProvider('javascript', {
            provideCompletionItems: (model: any, position: any) => {
                // find out if we are completing a property in the 'dependencies' object.
                let textUntilPosition = model.getValueInRange({
                    startLineNumber: 1,
                    startColumn: 1,
                    endLineNumber: position.lineNumber,
                    endColumn: position.column,
                });
                let word = model.getWordUntilPosition(position);
                let range = {
                    startLineNumber: position.lineNumber,
                    endLineNumber: position.lineNumber,
                    startColumn: word.startColumn,
                    endColumn: word.endColumn,
                };
                return {
                    suggestions: this.createDependencyProposals(range, null),
                };
            },
        });

        this.editor.onDidChangeModelContent((event: any) => {
            this.invalidSyntax = true;
        });

        this.registerFocusBlurEvents();
        this.evalFormula();
    }

    /**
     * @param range
     * @param fieldName
     * @returns
     */
    createDependencyProposals = (range: any, fieldName: any) => {
        // returning a static list of proposals, not even looking at the prefix (filtering is done by the Monaco editor),
        // here you could do a server side lookup

        let data = getOriginalFunctions(range);

        if (this.originalFunctionNames.length == 0) {
            this.originalFunctionNames = data.map((f) => {
                if (f.label !== 'Length') {
                    return f.label;
                }
                return null;
            });
        }

        if (this.fields != null && this.fields.length > 0) {
            for (let i = 0; i < this.fields.length; i++) {
                if (fieldName) {
                    if (fieldName === this.fields[i].value) {
                        continue;
                    }
                }
                if (this.fields[i].value.toString() === this.fieldName)
                    continue;
                data.push({
                    label: this.fields[i].value.toString(),
                    kind: (window as any).monaco.languages.CompletionItemKind
                        .Field,
                    documentation: 'This is the field from the current table.',
                    insertText: this.fields[i].value.toString(),
                    range: range,
                });
            }
        }
        return data;
    };

    /**
     * @param item
     */
    addToFormula = (item: any) => {
        let field = '[' + item.value + ']';
        let data = this.editor.getValue();
        let position = this.editor.getPosition();
        this.isFormulaChanged = true;

        if (position.lineNumber == 1) {
            let firstHalf = data.substring(0, position.column - 1);
            let secondHalf = data.substring(position.column - 1);
            data = firstHalf + field + secondHalf;
        } else if (position.lineNumber > 1) {
            let strs = data.split('\n');
            let cursorPosition = 0;
            for (let i = 0; i < strs.length; i++) {
                if (position.lineNumber - 1 == i) {
                    cursorPosition += position.column;
                    break;
                } else {
                    cursorPosition += strs[i].length;
                }
            }
            cursorPosition += position.lineNumber - 2;
            let firstHalf = data.substring(0, cursorPosition);
            let secondHalf = data.substring(cursorPosition);
            data = firstHalf + field + secondHalf;
        }

        let positionBeforeDoubleClicked = this.editor.getPosition();
        positionBeforeDoubleClicked.column += field.length;

        let fullRange = this.editor.getModel().getFullModelRange();

        // Apply the text over the range
        this.editor.executeEdits(null, [
            {
                text: data,
                range: fullRange,
            },
        ]);

        this.editor.setPosition(positionBeforeDoubleClicked);
        this.editor.focus();
        this.form.controls.Formula.setValue(data);
        this.evalFormula();
    };

    /**
     * @param stack
     * @returns
     */
    peek = (stack: Array<string>) => {
        return stack[stack.length - 1];
    };

    isSyntaxValid = () => {
        if (
            this.syntaxErrors ||
            this.fieldErrors ||
            this.functionErrors ||
            this.operatorErrors ||
            this.htmlTagErrors
        ) {
            this.invalidSyntax = true;
        } else {
            this.invalidSyntax = false;
        }
    };

    customPatternValidator = (control: AbstractControl) => {
        const value: string = control.value;
        if (value && value.toLowerCase().startsWith('related')) {
            this.matchMessage =
                'Related is not allowed at the beginning of field name.';
            return { mustMatch: true };
        }
        if (
            value &&
            (value.toLowerCase()?.trim()?.replace(/\s+/g, ' ') ===
                'record id' ||
                value.toLowerCase()?.trim()?.replace(/\s+/g, ' ') ===
                'created by' ||
                value.toLowerCase()?.trim()?.replace(/\s+/g, ' ') ===
                'modified by' ||
                value.toLowerCase()?.trim()?.replace(/\s+/g, ' ') ===
                'date created' ||
                value.toLowerCase()?.trim()?.replace(/\s+/g, ' ') ===
                'date modified')
        ) {
            this.matchMessage = 'Field name must be unique.';
            return { mustMatch: true };
        }
        // else if (value && value.toLowerCase().endsWith(' url')) {
        //   this.matchMessage = "URL is not allowed at the end of field name."
        //   return { mustMatch: true };
        // }
        return null;
    };

    customLookupNameValidator = (control: AbstractControl) => {
        const value: string = control.value;
        const fieldNames = this.fields?.map((field) => field.label);
        if (value && fieldNames?.includes(value)) {
            this.matchMessage = 'Field Name already Exists.';
            return { mustMatch: true };
        }
        // else if (value && value.toLowerCase().endsWith(' url')) {
        //   this.matchMessage = "URL is not allowed at the end of field name."
        //   return { mustMatch: true };
        // }
        return null;
    };

    findName = (
        lastIndex: number,
        chars: Array<string>,
        formula: string,
        forHtml: boolean = false
    ) => {
        let list = (forHtml ? '<' : '(,+-*/.\n\r<>=^').split('');
        for (let i = lastIndex; i >= 0; i--) {
            let c = chars[i];
            if (list.includes(c)) {
                return formula.substring(i + 1, lastIndex + 1).trim();
            }
        }

        return formula.substring(0, lastIndex + 1).trim();
    };

    registerFocusBlurEvents = () => {
        if (this.editor == undefined || this.editor == null) return;

        this.editor.onDidFocusEditorWidget(() => {
            let data = this.editor.getValue();
            let fullRange = this.editor.getModel().getFullModelRange();

            // Apply the text over the range
            this.editor.executeEdits(null, [
                {
                    text: data,
                    range: fullRange,
                },
            ]);

            this.form.controls.Formula.setValue(data);
            this.evalFormula();
        });

        this.editor.onDidBlurEditorWidget(() => {
            let data = this.editor.getValue();
            let fullRange = this.editor.getModel().getFullModelRange();

            // Apply the text over the range
            this.editor.executeEdits(null, [
                {
                    text: data,
                    range: fullRange,
                },
            ]);

            this.form.controls.Formula.setValue(data);
            this.evalFormula();
        });

        this.editor.onDidChangeModelDecorations(() => {
            this.updateEditorForMarkerAndDecorations();
        });
    };

    updateEditorForMarkerAndDecorations = () => {
        let model = this.editor.getModel();
        if (model === null || model.getModeId() !== 'javascript') return;

        let owner = model.getModeId();
        let markers = (window as any).monaco.editor.getModelMarkers({
            owner: owner,
        });

        if (
            this.currentOperatorNames.length > 0 ||
            this.currentNotEqualExpressions.length > 0
        ) {
            let jsMarkers = (window as any).monaco.editor.getModelMarkers({
                owner: 'javascript',
            });
            let tempMarkers: any[] = [];

            jsMarkers.forEach((marker: any) => {
                let shouldAdd = true;

                // Handle ',' expected markers (1005)
                if (
                    marker.code == '1005' &&
                    marker.message == "',' expected."
                ) {
                    this.currentOperatorNames.forEach((op) => {
                        if (
                            marker.startLineNumber == op[3] &&
                            marker.startColumn == op[1] &&
                            marker.endColumn == op[2]
                        ) {
                            shouldAdd = false; // Markers related to currentOperatorNames
                        }
                    });
                }

                // Handle ')' expected markers (1005)
                if (
                    marker.code == '1005' &&
                    (marker.message == "')' expected." || marker.message == "';' expected.")
                ) {
                    shouldAdd = false;
                }

                // Handle 'Expression expected.' markers (1109)
                if (
                    marker.code == '1109' &&
                    marker.message == 'Expression expected.'
                ) {
                    this.currentNotEqualExpressions.forEach(
                        (
                            notEqualExpression: [String, Number, Number, Number]
                        ) => {
                            if (
                                marker.startColumn == notEqualExpression[1] &&
                                marker.startLineNumber ==
                                notEqualExpression[3] &&
                                marker.endColumn == notEqualExpression[2] &&
                                marker.endLineNumber == notEqualExpression[3]
                            ) {
                                shouldAdd = false; // Markers related to currentNotEqualExpressions
                            }
                        }
                    );
                }

                // Add marker if it does not match exclusions
                if (shouldAdd) {
                    tempMarkers.push(marker);
                }
            });

            // Set updated markers
            (window as any).monaco.editor.setModelMarkers(
                (window as any).monaco.editor.getModels()[0],
                'javascript',
                tempMarkers
            );
            markers = tempMarkers;
        }

        // do something with the markers
        let javaScriptDecorations: Array<any> = [];
        let ranges: Array<any> = [];
        markers.forEach((marker: any) => {
            ranges.push({
                startLineNumber: marker.startLineNumber,
                startColumn: marker.startColumn,
                endLineNumber: marker.endLineNumber,
                endColumn: marker.endColumn,
            });
            javaScriptDecorations.push({
                range: new (window as any).monaco.Range(
                    marker.startLineNumber,
                    marker.startColumn,
                    marker.endLineNumber,
                    marker.endColumn
                ),
                options: {
                    isWholeLine: true,
                    glyphMarginClassName: 'errorIcon',
                },
            });
        });

        if (markers.length > 0) this.noMarkersCount = 0;

        if (this.oldJavaScriptsRanges.length == 0) {
            this.oldJavaScriptsRanges = ranges;
        } else {
            if (ranges.length === this.oldJavaScriptsRanges.length) {
                let shouldReturn = true;
                for (let i = 0; i < ranges.length; i++) {
                    let oldRange = this.oldJavaScriptsRanges[i];
                    let newRange = ranges[i];
                    if (
                        oldRange.startLineNumber != newRange.startLineNumber ||
                        oldRange.endLineNumber != newRange.endLineNumber ||
                        oldRange.startColumn != newRange.startColumn ||
                        oldRange.endColumn != newRange.endColumn
                    ) {
                        shouldReturn = false;
                        break;
                    }
                }

                if (shouldReturn) return;
            }
        }

        this.oldJavaScriptsRanges = ranges;

        if (
            (this.oldJavaScriptDecorations.length > 0 ||
                javaScriptDecorations.length > 0) &&
            this.noMarkersCount == 0
        ) {
            this.noMarkersCount++;
            this.oldJavaScriptDecorations = this.editor.deltaDecorations(
                this.oldJavaScriptDecorations,
                javaScriptDecorations
            );
        }

        if (javaScriptDecorations.length > 0) {
            this.syntaxErrors = true;
        } else {
            this.syntaxErrors = false;
        }

        this.isSyntaxValid();
    };

    evalFormula = () => {
        let formulaFields: Array<string> = [];
        let str = this.form.controls.Formula.value;
        this.form.controls.EvalFormula.setValue(str.replace(/[\[\]]+/g, ''));
        this.isFormulaChanged = true;
        let number_regex = /\[(\w+)\]/g;
        str.replace(number_regex, (match: any) => {
            let field_name = match.replace(/[\[\]]+/g, '');
            formulaFields.push(field_name);
        });
        this.form.controls.FormulaFields.setValue(
            [...new Set(formulaFields)].join()
        );
        this.checkFormulaSyntax(this.form.controls.Formula.value);

        // Commenting the formula verification until we're done with fixing all the prod failed formula issues
        // this.isValidSyntax(this.form.controls.Formula.value);
    };

    isValidSyntax(formula: string): void {
        const methods = Array.from(methodsWithParam.keys());
        const methodOccurrences = new Map<string, number>();

        const parenthesesStack: Array<[string, number, number]> = [];
        const expressionParenthesesStack: Array<[string, number, number]> = [];
        const squareBracketStack: Array<[string, number, number]> = [];
        const quotesStack: Array<[string, number, number]> = [];
        const methodParamDatas: MethodParamData[] = [];
        const isExpressionParenthesesStack: Array<boolean> = [];

        let insideString = false;
        let paramCount = 0;
        let line = 1;
        let column = 1;
        let isExpressionWithParantheses = false;

        for (let i = 0; i < formula.length; i++) {
            const c = formula[i];

            column++;

            if (c === '\n') {
                line++;
                column = 1;
            }

            if (
                (c === '"' || c === "'") &&
                (i === 0 || formula[i - 1] !== '\\') &&
                ((insideString &&
                    quotesStack.length > 0 &&
                    quotesStack[quotesStack.length - 1][0] === c) ||
                    !insideString)
            ) {
                insideString = !insideString;

                if (insideString) {
                    quotesStack.push([c, line, column]);
                } else {
                    if (quotesStack.length === 0) {
                        this.registerError(
                            line,
                            column,
                            'Error : Unmatched closing quote, please check!'
                        );
                        return;
                    }
                    quotesStack.pop();
                }
            }

            if (!insideString) {
                if (c === '(') {
                    const subStr = formula.slice(0, i);
                    const method = methods.find((m) =>
                        subStr.trim().endsWith(m)
                    );
                    if (method) {
                        parenthesesStack.push([c, line, column]);
                        isExpressionWithParantheses = false;
                        isExpressionParenthesesStack.push(
                            isExpressionWithParantheses
                        );

                        methodOccurrences.set(
                            method,
                            (methodOccurrences.get(method) || 0) + 1
                        );

                        const methodParam: MethodParamData = {
                            Method: method,
                            Params: 1,
                            ParamsPositions: [],
                        };
                        methodParam.ParamsPositions.push({
                            StartLine: line,
                            StartColumn: column + 1,
                        });
                        methodParamDatas.push(methodParam);
                    } else {
                        expressionParenthesesStack.push([c, line, column]);
                        isExpressionWithParantheses = true;
                        isExpressionParenthesesStack.push(
                            isExpressionWithParantheses
                        );
                    }
                } else if (c === ')') {
                    isExpressionWithParantheses =
                        isExpressionParenthesesStack.length > 0 &&
                        isExpressionParenthesesStack[
                        isExpressionParenthesesStack.length - 1
                        ];

                    if (isExpressionWithParantheses) {
                        if (expressionParenthesesStack.length == 0) {
                            this.registerError(
                                line,
                                column,
                                "Error : Unmatched expression's closing parenthesis, please check!"
                            );
                            return;
                        }

                        isExpressionWithParantheses = false;
                        expressionParenthesesStack.pop();
                    } else {
                        if (parenthesesStack.length === 0) {
                            this.registerError(
                                line,
                                column,
                                'Error : Unmatched closing parenthesis, please check!'
                            );
                            return;
                        }
                        parenthesesStack.pop();

                        if (formula[i - 1] !== '(') {
                            paramCount++;

                            if (methodParamDatas.length === 0) {
                                this.registerError(
                                    line,
                                    column,
                                    'Error : Unexpected closing parenthesis, please check!'
                                );
                                return;
                            }

                            const methodParam = methodParamDatas.pop();
                            if (
                                methodParam &&
                                methodParam.Params >
                                methodsWithParam.get(methodParam.Method)!
                            ) {
                                const overloadedMethodKey = `${methodParam.Method}_${methodParam.Params}`;

                                if (methodsWithParam.has(overloadedMethodKey)) {
                                    methodOccurrences.set(
                                        overloadedMethodKey,
                                        methodOccurrences.has(
                                            overloadedMethodKey
                                        )
                                            ? methodOccurrences.get(
                                                overloadedMethodKey
                                            )! + 1
                                            : 1
                                    );

                                    const methodCount = methodOccurrences.get(
                                        methodParam.Method
                                    );

                                    if (methodCount && methodCount > 0) {
                                        methodOccurrences.set(
                                            methodParam.Method,
                                            methodCount - 1
                                        );
                                    }
                                } else {
                                    this.registerError(
                                        line,
                                        column,
                                        'Error : Unexpected closing parenthesis, please check!'
                                    );
                                    return;
                                }
                            }
                        } else {
                            if (methodParamDatas.length === 0) {
                                this.registerError(
                                    line,
                                    column,
                                    'Error : Unexpected closing parenthesis, please check!'
                                );
                                return;
                            }

                            const methodParam = methodParamDatas.pop();
                            if (methodParam) {
                                const lastParam =
                                    methodParam.ParamsPositions[
                                    methodParam.ParamsPositions.length - 1
                                    ];
                                if (
                                    lastParam.StartLine === line &&
                                    lastParam.StartColumn === column
                                ) {
                                    methodParam.Params--;
                                }

                                if (
                                    methodParam &&
                                    methodParam.Params >
                                    methodsWithParam.get(
                                        methodParam.Method
                                    )!
                                ) {
                                    const overloadedMethodKey = `${methodParam.Method}_${methodParam.Params}`;
                                    if (
                                        methodsWithParam.has(
                                            overloadedMethodKey
                                        )
                                    ) {
                                        methodOccurrences.set(
                                            overloadedMethodKey,
                                            (methodOccurrences.get(
                                                overloadedMethodKey
                                            ) || 0) + 1
                                        );

                                        if (
                                            methodOccurrences.get(
                                                methodParam.Method
                                            )
                                        ) {
                                            methodOccurrences.set(
                                                methodParam.Method,
                                                (methodOccurrences.get(
                                                    methodParam.Method
                                                ) || 0) - 1
                                            );
                                        }
                                    } else {
                                        this.registerError(
                                            line,
                                            column,
                                            'Error : Unexpected closing parenthesis, please check!'
                                        );
                                        return;
                                    }
                                }
                            }
                        }
                    }

                    if (isExpressionParenthesesStack.length > 0)
                        isExpressionParenthesesStack.pop();
                } else if (c === ',' && !isExpressionWithParantheses) {
                    if (methodParamDatas.length === 0) {
                        this.registerError(
                            line,
                            column,
                            'Error : Unexpected comma, please check!'
                        );
                        return;
                    }

                    const methodParam =
                        methodParamDatas[methodParamDatas.length - 1];
                    methodParam.Params++;
                    if (
                        methodParam.Params >
                        methodsWithParam.get(methodParam.Method)!
                    ) {
                        const overloadedMethodKey = `${methodParam.Method}_${methodParam.Params}`;

                        if (!methodsWithParam.has(overloadedMethodKey)) {
                            this.registerError(
                                line,
                                column,
                                'Error : Unexpected comma, please check!'
                            );

                            return;
                        }
                    }

                    const lastParam =
                        methodParam.ParamsPositions[
                        methodParam.ParamsPositions.length - 1
                        ];
                    lastParam.EndLine = line;
                    lastParam.EndColumn = column - 1;

                    methodParam.ParamsPositions.push({
                        StartLine: line,
                        StartColumn: column + 1,
                    });

                    paramCount++;
                } else if (c === '[') {
                    squareBracketStack.push([c, line, column]);
                } else if (c === ']') {
                    if (squareBracketStack.length === 0) {
                        this.registerError(
                            line,
                            column,
                            'Error : Unmatched closing square bracket, please check!'
                        );
                        return;
                    }
                    squareBracketStack.pop();
                }
            }
        }

        let totalExpectedParamCount = 0;
        for (const [method, count] of methodOccurrences) {
            if (!methodsWithParam.has(method)) {
                this.registerError(
                    line,
                    column,
                    'Error : Missing method, please check!'
                );
                return;
            }
            totalExpectedParamCount += methodsWithParam.get(method)! * count;
        }

        if (parenthesesStack.length > 0) {
            const unmatched = parenthesesStack[parenthesesStack.length - 1];
            this.registerError(
                unmatched[1],
                unmatched[2],
                'Error : Unmatched opening parenthesis, please check!'
            );
            return;
        }

        if (expressionParenthesesStack.length > 0) {
            const unmatched =
                expressionParenthesesStack[
                expressionParenthesesStack.length - 1
                ];
            this.registerError(
                unmatched[1],
                unmatched[2],
                "Error : Unmatched expression's opening parenthesis, please check!"
            );
            return;
        }

        if (quotesStack.length > 0) {
            const unmatched = quotesStack[quotesStack.length - 1];
            this.registerError(
                unmatched[1],
                unmatched[2],
                'Error : Unmatched opening quote, please check!'
            );
            return;
        }

        if (squareBracketStack.length > 0) {
            const unmatched = squareBracketStack[squareBracketStack.length - 1];
            this.registerError(
                unmatched[1],
                unmatched[2],
                'Error : Unmatched opening square bracket, please check!'
            );
            return;
        }

        if (paramCount !== totalExpectedParamCount) {
            this.registerError(
                line,
                column,
                'Error : Missing parameters, please check!'
            );
            return;
        }
    }

    checkFormulaSyntax = (formula: string) => {
        let chars = formula.split('');
        let ops: Array<string> = [];
        let tags: Array<string> = [];
        let operands = [];
        let fieldNames = new Array<[string, number, number, number]>();
        let functionNames = new Array<[string, number, number, number]>();
        let htmlTagNames = new Array<[string, number, number, number]>();
        let operatorNames = new Array<[string, number, number, number]>();
        let notEqualToExpressions = new Array<
            [string, number, number, number]
        >();
        let indexesToPutSpace = new Array<[number, number]>();
        let startIndexOfField = -1;
        let startPositionOfField = -1;
        let line = 1;
        let position = 0;
        let encounteredNonSpaceCharacter = false;
        let lastNonSpaceCharacter = ' ';
        let characterPosition = 0;
        let operators = [];

        for (let i = 0; i < chars.length; i++, characterPosition++) {
            let c = chars[i];
            if (c == '\n') {
                line++;
                position = 0;
                characterPosition = 0;
                encounteredNonSpaceCharacter = false;
            }

            if (
                c != '\r' &&
                c != '\n' &&
                c != ' ' &&
                c != '\t' &&
                !encounteredNonSpaceCharacter
            )
                encounteredNonSpaceCharacter = true;

            if (c == '(' || c == '[') {
                if (!ops.includes('"')) {
                    // if (
                    //     ops.length > 0 &&
                    //     c == '(' &&
                    //     lastNonSpaceCharacter == c
                    // ) {
                    //     return [line, position, c];
                    // } else 
                    if (
                        ops.length > 0 &&
                        c == '[' &&
                        this.peek(ops) == '['
                    ) {
                        return [line, i, c];
                    }
                    ops.push(c);
                    operands.push([line, i, c]);
                }

                if (c == '[') {
                    startIndexOfField = i + 1;
                    startPositionOfField = characterPosition;
                }
                if (c == '(' && i > 0 && this.peek(ops) == '(') {
                    let functionName = this.findName(i - 1, chars, formula);
                    if (functionName === '') continue;
                    let characterPositionOfFunction =
                        characterPosition - functionName.length - 1;
                    functionNames.push([
                        functionName,
                        characterPositionOfFunction,
                        characterPositionOfFunction + functionName.length,
                        line,
                    ]);
                }
            } else if (c == ')') {
                if (
                    ops.length > 0 &&
                    this.peek(ops) == '(' &&
                    !ops.includes('"')
                ) {
                    ops.pop();
                    operands.pop();
                } else if (
                    ops.length == 0 ||
                    (ops.length > 0 &&
                        this.peek(ops) != '(' &&
                        !ops.includes('"'))
                ) {
                    return [line, i, c];
                }
            } else if (c == ']') {
                if (
                    ops.length > 0 &&
                    this.peek(ops) == '[' &&
                    !ops.includes('"')
                ) {
                    let fieldName = formula.substring(startIndexOfField, i);
                    startPositionOfField =
                        line == 1
                            ? startPositionOfField + 1
                            : startPositionOfField;
                    let characterPositionOfField =
                        line == 1 ? characterPosition + 1 : characterPosition;
                    fieldNames.push([
                        fieldName,
                        startPositionOfField,
                        characterPositionOfField - 1,
                        line,
                    ]);

                    ops.pop();
                    operands.pop();
                } else if (
                    ops.length == 0 ||
                    (ops.length > 0 &&
                        this.peek(ops) != '[' &&
                        !ops.includes('"'))
                ) {
                    return [line, i, c];
                }
            } else if (c == '"') {
                if (ops.length > 0 && this.peek(ops) == c) {
                    ops.pop();
                    operands.pop();
                } else {
                    ops.push(c);
                    operands.push([line, i, c]);
                }
            } else if (c == '=') {
                if (chars[i - 1] == '=') {
                    let characterPositionOfOperator =
                        line == 1 ? characterPosition : characterPosition - 1;
                    let arr = [
                        characterPositionOfOperator - 1,
                        characterPositionOfOperator + 1,
                        line,
                    ];
                    operators.push(arr);
                }
            } else if (c == ' ') {
                if (
                    i + 3 < chars.length &&
                    chars[i + 1] == 'A' &&
                    chars[i + 2] == 'N' &&
                    chars[i + 3] == 'D' &&
                    chars[i + 4] == ' '
                ) {
                    operatorNames.push([
                        'AND',
                        characterPosition + (line == 1 ? 2 : 1),
                        characterPosition + (line == 1 ? 5 : 4),
                        line,
                    ]);
                } else if (
                    i + 2 < chars.length &&
                    chars[i + 1] == 'O' &&
                    chars[i + 2] == 'R' &&
                    chars[i + 3] == ' '
                ) {
                    operatorNames.push([
                        'OR',
                        characterPosition + (line == 1 ? 2 : 1),
                        characterPosition + (line == 1 ? 4 : 3),
                        line,
                    ]);
                }

                if (
                    i + 3 < chars.length &&
                    chars[i + 1] == 'A' &&
                    chars[i + 2] == 'N' &&
                    chars[i + 3] == 'D' &&
                    chars[i + 4] != ' '
                ) {
                    indexesToPutSpace.push([i + 3 + 1, line]);
                } else if (
                    i + 2 < chars.length &&
                    chars[i + 1] == 'O' &&
                    chars[i + 2] == 'R' &&
                    chars[i + 3] != ' '
                ) {
                    indexesToPutSpace.push([i + 2 + 1, line]);
                }
            } else if (c == '<') {
                if (ops.includes('"')) {
                    tags.push('<');
                    operands.push([line, i, c]);
                }
            } else if (c == '>') {
                if (
                    ops.includes('"') &&
                    tags.includes('<') &&
                    this.peek(tags) == '<'
                ) {
                    tags.pop();
                    let htmlTagName = this.findName(
                        i - 1,
                        chars,
                        formula,
                        true
                    );
                    let length = htmlTagName.length;
                    if (htmlTagName.startsWith('/'))
                        htmlTagName = htmlTagName.substring(1);
                    if (htmlTagName.includes(' '))
                        htmlTagName = htmlTagName.split(' ')[0];
                    let characterPositionOfFunction =
                        characterPosition - length - 1;
                    htmlTagNames.push([
                        htmlTagName,
                        characterPositionOfFunction,
                        characterPositionOfFunction + htmlTagName.length,
                        line,
                    ]);
                }

                if (i > 0 && chars[i - 1] == '<') {
                    notEqualToExpressions.push([
                        '<>',
                        line == 1 ? characterPosition + 1 : characterPosition,
                        line == 1
                            ? characterPosition + 2
                            : characterPosition + 1,
                        line,
                    ]);
                }
            }

            if (encounteredNonSpaceCharacter) position++;

            if (c != '\r' && c != '\n' && c != ' ') lastNonSpaceCharacter = c;
        }

        if (fieldNames.length > 0) {
            this.form.controls['FormulaFields'].setValidators([
                Validators.required,
            ]);
            this.form.controls['FormulaFields'].updateValueAndValidity();
            let fieldNamesFromTable = this.fields?.map((f) => {
                return f.value;
            });

            let modelMarkers: Array<any> = [];
            let decorations: Array<any> = [];
            fieldNames.forEach((field: [string, number, number, number]) => {
                if (!fieldNamesFromTable?.includes(field[0])) {
                    modelMarkers.push({
                        startLineNumber: field[3],
                        startColumn: field[1] + 1,
                        endLineNumber: field[3],
                        endColumn: field[2] + 1,
                        message: 'Error : Unknown field name, please check!',
                        severity: (window as any).monaco.MarkerSeverity.Error,
                    });
                    decorations.push({
                        range: new (window as any).monaco.Range(
                            field[3],
                            field[1],
                            field[3],
                            field[2]
                        ),
                        options: {
                            isWholeLine: true,
                            glyphMarginClassName: 'errorIcon',
                        },
                    });
                } else if (this.toBeUpdated() && field[0] == this.fieldName) {
                    modelMarkers.push({
                        startLineNumber: field[3],
                        startColumn: field[1] + 1,
                        endLineNumber: field[3],
                        endColumn: field[2] + 1,
                        message:
                            "Error : Can't use the same field in formula which we are updating, please check!",
                        severity: (window as any).monaco.MarkerSeverity.Error,
                    });
                    decorations.push({
                        range: new (window as any).monaco.Range(
                            field[3],
                            field[1],
                            field[3],
                            field[2]
                        ),
                        options: {
                            isWholeLine: true,
                            glyphMarginClassName: 'errorIcon',
                        },
                    });
                }
            });

            if (this.oldFieldDecorations.length > 0 || decorations.length > 0) {
                this.oldFieldDecorations = this.editor.deltaDecorations(
                    this.oldFieldDecorations,
                    decorations
                );
                (window as any).monaco.editor.setModelMarkers(
                    (window as any).monaco.editor.getModels()[0],
                    'fields',
                    modelMarkers
                );
            }

            if (decorations.length > 0 || modelMarkers.length > 0) {
                this.fieldErrors = true;
            } else {
                this.fieldErrors = false;
            }

            this.isSyntaxValid();
        } else {
            this.fieldErrors = false;
            this.oldFieldDecorations = this.editor.deltaDecorations(
                this.oldFieldDecorations,
                []
            );
            (window as any).monaco.editor.setModelMarkers(
                (window as any).monaco.editor.getModels()[0],
                'fields',
                []
            );
            this.isSyntaxValid();
            this.form.controls['FormulaFields'].clearValidators();
            this.form.controls['FormulaFields'].updateValueAndValidity();
        }

        if (functionNames.length > 0) {
            let modelMarkers: Array<any> = [];
            let decorations: Array<any> = [];

            if (this.originalFunctionNames.length == 0) {
                let data = getOriginalFunctions(null);
                this.originalFunctionNames = data.map((f) => {
                    if (f.label !== 'Length') {
                        return f.label;
                    }
                    return null;
                });
            }

            functionNames.forEach((func: [string, number, number, number]) => {
                if (!this.originalFunctionNames.includes(func[0])) {
                    modelMarkers.push({
                        startLineNumber: func[3],
                        startColumn: func[1] + 1,
                        endLineNumber: func[3],
                        endColumn: func[2] + 1,
                        message: 'Error : Unknown function name, please check!',
                        severity: (window as any).monaco.MarkerSeverity.Error,
                    });
                    decorations.push({
                        range: new (window as any).monaco.Range(
                            func[3],
                            func[1],
                            func[3],
                            func[2]
                        ),
                        options: {
                            isWholeLine: true,
                            glyphMarginClassName: 'errorIcon',
                        },
                    });
                }
            });

            if (
                this.oldFunctionDecorations.length > 0 ||
                decorations.length > 0
            ) {
                this.oldFunctionDecorations = this.editor.deltaDecorations(
                    this.oldFunctionDecorations,
                    decorations
                );
                (window as any).monaco.editor.setModelMarkers(
                    (window as any).monaco.editor.getModels()[0],
                    'functions',
                    modelMarkers
                );
            }

            if (decorations.length > 0 || modelMarkers.length > 0) {
                this.functionErrors = true;
            } else {
                this.functionErrors = false;
            }

            this.isSyntaxValid();
        } else {
            this.functionErrors = false;
            this.isSyntaxValid();
        }

        if (htmlTagNames.length > 0) {
            this.isHtml = true;
            let modelMarkers: Array<any> = [];
            let decorations: Array<any> = [];
            htmlTagNames.forEach((func: [string, number, number, number]) => {
                if (!allowedHtmlTags.includes(func[0])) {
                    modelMarkers.push({
                        startLineNumber: func[3],
                        startColumn: func[1] + 1,
                        endLineNumber: func[3],
                        endColumn: func[2] + 1,
                        message:
                            'Error : This HTML tag is not allowed, please check the Limited HTML tag list!',
                        severity: (window as any).monaco.MarkerSeverity.Error,
                    });
                    decorations.push({
                        range: new (window as any).monaco.Range(
                            func[3],
                            func[1],
                            func[3],
                            func[2]
                        ),
                        options: {
                            isWholeLine: true,
                            glyphMarginClassName: 'errorIcon',
                        },
                    });
                }
            });

            if (
                this.oldHtmlTagDecorations.length > 0 ||
                decorations.length > 0
            ) {
                this.oldHtmlTagDecorations = this.editor.deltaDecorations(
                    this.oldHtmlTagDecorations,
                    decorations
                );
                (window as any).monaco.editor.setModelMarkers(
                    (window as any).monaco.editor.getModels()[0],
                    'htmlTags',
                    modelMarkers
                );
            }

            if (decorations.length > 0 || modelMarkers.length > 0) {
                this.htmlTagErrors = true;
            } else {
                this.htmlTagErrors = false;
            }

            this.isSyntaxValid();
        } else {
            this.isHtml = false;
            this.htmlTagErrors = false;
            this.isSyntaxValid();
        }

        if (operators.length >= 0) {
            let modelMarkers: Array<any> = [];
            let decorations: Array<any> = [];

            operators.forEach((operator) => {
                modelMarkers.push({
                    startLineNumber: operator[2],
                    startColumn: operator[0] + 1,
                    endLineNumber: operator[2],
                    endColumn: operator[1] + 1,
                    message:
                        'Error : Only single “=” is allowed for comparision!',
                    severity: (window as any).monaco.MarkerSeverity.Error,
                });
                decorations.push({
                    range: new (window as any).monaco.Range(
                        operator[2],
                        operator[0],
                        operator[2],
                        operator[1]
                    ),
                    options: {
                        isWholeLine: true,
                        glyphMarginClassName: 'errorIcon',
                    },
                });
            });

            if (
                this.oldOperatorDecorations.length > 0 ||
                decorations.length > 0
            ) {
                this.oldOperatorDecorations = this.editor.deltaDecorations(
                    this.oldOperatorDecorations,
                    decorations
                );
                (window as any).monaco.editor.setModelMarkers(
                    (window as any).monaco.editor.getModels()[0],
                    'operators',
                    modelMarkers
                );
            }

            if (decorations.length > 0 || modelMarkers.length > 0) {
                this.operatorErrors = true;
            } else {
                this.operatorErrors = false;
            }

            this.isSyntaxValid();
        } else {
            this.operatorErrors = false;
            this.isSyntaxValid();
        }

        if (operatorNames.length > 0) {
            let markers = (window as any).monaco.editor.getModelMarkers({
                owner: 'javascript',
            });
            let tempMarkers: any[] = [];
            markers.forEach((marker: any) => {
                if (
                    marker.code == '1005' &&
                    marker.message == "',' expected."
                ) {
                    let isAndOrOperator = false;
                    operatorNames.forEach(function (op) {
                        if (
                            marker.startLineNumber == op[3] &&
                            marker.startColumn == op[1] &&
                            marker.endColumn == op[2]
                        ) {
                            isAndOrOperator = true;
                        }
                    });
                    if (!isAndOrOperator) {
                        tempMarkers.push(marker);
                    }
                } else {
                    tempMarkers.push(marker);
                }
            });
            (window as any).monaco.editor.setModelMarkers(
                (window as any).monaco.editor.getModels()[0],
                'javascript',
                tempMarkers
            );

            this.isSyntaxValid();
        }

        if (indexesToPutSpace.length > 0) {
            indexesToPutSpace.forEach(function (ind, index) {
                formula =
                    formula.slice(0, ind[0] + index) +
                    ' ' +
                    formula.slice(ind[0] + index);
            });

            let fullRange = this.editor.getModel().getFullModelRange();
            // Apply the text over the range
            this.editor.executeEdits(null, [
                {
                    text: formula,
                    range: fullRange,
                },
            ]);
        }

        if (operatorNames.length > 0) {
            this.currentOperatorNames = [...operatorNames];

            this.updateEditorForMarkerAndDecorations();
        } else {
            this.currentOperatorNames = [];
        }

        if (notEqualToExpressions.length > 0) {
            this.currentNotEqualExpressions = [...notEqualToExpressions];

            this.updateEditorForMarkerAndDecorations();
        } else {
            this.currentNotEqualExpressions = [];
        }

        if (operands.length > 0) {
            return operands[0];
        }

        return null;
    };

    /**
     * Patch forms value if edit request (or if id is passed)
     */
    patchEditValues = (fields: Array<Field>) => {
        const field = fields.find((field) => field.Key === this.key);
        if (this.isRelationSelected())
            this.relationFieldType = field?.FieldType;
        if (field) {
            this.valueControl.clear();
            let fieldType: any = '';
            if (field.IsFormula) {
                let type = this.fieldTypes?.find(
                    (type) =>
                        type.label.toLowerCase().includes('formula') &&
                        type.value2 === field.FieldType
                )?.label;

                fieldType =
                    type == 'Formula - URL' && !field.IsUrl
                        ? 'Formula - Text'
                        : type;
            } else {
                fieldType = this.fieldTypes?.find(
                    (type) =>
                        !type.label.toLowerCase().includes('formula') &&
                        type.value2 === field.FieldType
                )?.label;
                if (field.FieldType === 'VARCHAR(255)' && field.IsUrl)
                    fieldType = 'URL';
                if (
                    field.FieldType === 'VARCHAR' &&
                    !field.IsUrl &&
                    !field.IsFormula
                )
                    fieldType = 'Text';
                if (
                    field.FieldType === 'TEXT' &&
                    !field.IsUrl &&
                    !field.IsFormula
                )
                    fieldType = 'Text - Multi-line';
            }
            this.form.patchValue(
                {
                    ...field,
                    FieldType: fieldType,
                    FormulaFields: field.FormulaFields?.split(','),
                    Values: field.Values?.sort((a: any, b: any) => {
                        if (a.Order < b.Order) {
                            return -1;
                        }
                        if (a.Order > b.Order) {
                            return 1;
                        }
                        return 0;
                    }).map((field) =>
                        this.addValue(
                            field.Value,
                            field.FieldKey,
                            field.IsActive
                        )
                    ),
                    isGroupSummaryType: field.OnGroupingSummaryType,
                    onGroupingSummaryType: field.OnGroupingSummaryType,
                    EnableDoc: field.EnableDoc,
                    FieldRoles:
                        fieldType === 'Encrypted (Standard)'
                            ? field.FieldRoles.map((roles: any) =>
                                roles.AppAdmins
                                    ? 'admins'
                                    : roles.ApplicationRoleKey
                            )
                            : null,
                },
                { emitEvent: false }
            );
            if (field.OnGroupingSummaryType) {
                this.form.controls.onGroupingSummaryType.setValidators(
                    Validators.required
                );
                this.form.controls.onGroupingSummaryType.updateValueAndValidity();
            }
            if (field.FieldType.toLowerCase().includes('file'))
                this.isFile = true;
            else this.isFile = false;

            this.fieldName = field.FieldName;
            this.isUnique = field.IsUnique;
            this.isNullable = field.IsNullable;
            this.isExternalUrl = field.IsExternalUrl;
            if (field.LookupTargetFieldKey) {
                this.form.controls.LookupDisplayName.setValidators([
                    Validators.maxLength(64),
                    this.customPatternValidator,
                    this.customLookupNameValidator,
                ]);
                if (
                    fieldType === 'Encrypted (Standard)' ||
                    fieldType === 'Encrypted (Extra Secure)'
                ) {
                    this.form.controls.FieldRoles.clearValidators();
                    this.form.controls.FieldRoles.updateValueAndValidity();
                    this.form.controls.PIILength.clearValidators();
                    this.form.controls.PIIDirection.clearValidators();
                }
            }
        }
    };

    /**
     * @returns if field is to be updated
     */
    toBeUpdated = () => !!this.key;

    /**
     * Submit
     */
    onSubmit = () => {
        if (!this.submitGuard.tryStart()) {
            return;
        }
        return this.toBeUpdated() ? this.onEditSubmit() : this.onAddSubmit();
    };

    onAddSubmit = async () => {
        try {
            let payload = this.requestPayload();
            if (payload) {
                await this.action.dispatch(
                    OpTypes.CREATE,
                    new CreateField(payload),
                    getFieldOperations
                );
                this.close();
            } else {
                this.submitGuard.reset();
            }
        } catch {
            this.submitGuard.reset();
        }
    };

    onEditSubmit = async () => {
        try {
            let payload = this.requestPayload();
            if (payload) {
                await this.action.dispatch(
                    OpTypes.UPDATE,
                    new UpdateField(
                        this.requestPayload(),
                        payload.LookupTargetFieldKey ? true : false
                    ),
                    getFieldOperations
                );
                this.close();
            } else {
                this.submitGuard.reset();
            }
        } catch {
            this.submitGuard.reset();
        }
    };

    /**
     * Generate request payload
     */
    requestPayload = () => {
        const value = { ...this.form.getRawValue() };
        if (value.IsFormula) {
            value.EvalFormula = value.Formula.replace(/(\[|\])/g, '');
            value.FormulaFields = value.FormulaFields.toString();
        } else {
            value.EvalFormula = null;
            value.FormulaFields = null;
        }

        value.IsUrl =
            value.FieldType === 'Formula - URL' || value.FieldType === 'URL';
        if (this.isFormulaChanged) {
            value.IsHtml = this.isHtml;
        }

        // set actual field type find by name
        value.FieldType = this.fieldTypes?.find(
            (type) => type.value === value.FieldType
        )?.value2;

        if (value.FieldType !== 'VARCHAR') {
            value.FieldSize = null;
        }

        if (value.FieldType === 'PII2') {
            value.FieldRoles = value.FieldRoles.map((roles: any) => {
                if (this.toBeUpdated()) {
                    return {
                        FieldKey: this.key,
                        AppAdmins: roles === 'admins' ? true : false,
                        ApplicationRoleKey: roles !== 'admins' ? roles : '',
                    };
                }
                return {
                    AppAdmins: roles === 'admins' ? true : false,
                    ApplicationRoleKey: roles !== 'admins' ? roles : '',
                };
            });
        }
        if (value.FieldType === 'ENUM' || value.FieldType === 'ENUM2') {
            const values = value.Values as Array<any>;
            if (values.length == 0) {
                this.alert.toast(
                    ToastTypes.ERROR,
                    'Error',
                    'Minimum One value required.'
                );
                return;
            }
            if (value.IsAlphaSort) {
                value.Values.sort((a: any, b: any) => {
                    return a.value
                        .toLowerCase()
                        .localeCompare(b.value.toLowerCase());
                });
            }
            value.Values = value.Values.map((enumValue: any, index: number) => {
                return {
                    ...enumValue,
                    value: enumValue?.value?.trim(),
                    Order: index + 1,
                };
            });
            if (!this.toBeUpdated()) {
                for (let value in values) delete values[value].FieldId;
            }
        }
        if (
            value.FieldType === undefined &&
            this.relationFieldType != undefined
        ) {
            value.FieldType = this.relationFieldType;
        }

        if (!value.isGroupSummaryType || !this.isNumericOrCurrencySelected()) {
            value.onGroupingSummaryType = null;
        }

        delete value.isGroupSummaryType;

        return value;
    };

    /**
     * Subscribers
     */
    loadSubscribers = () => {
        this.subsink.add(
            combineLatest([
                this.store.pipe(
                    select(getFieldTypes),
                    filter((types: any) => !!types)
                ),
                this.store.pipe(
                    select(getFields),
                    filter((fields: any) => !!fields[this.route.getTableKey()]),
                    map((fields: any) => fields[this.route.getTableKey()])
                ),
            ]).subscribe((data: any) => {
                this.fieldTypes = data[0]!
                    .filter((type: any) => this.isUuidEnabled || type.Type !== 'UUID')
                    .map((type: any) => {
                        // value should be ".Type" but there are multiple "Name"
                        // with same "Type" creating confusion
                        // we will be setting actual field type at the time of submit
                        return {
                            label: type.Name,
                            value: type.Name,
                            value2: type.Type,
                        };
                    })
                    .sort((firstType: any, secondType: any) => {
                        if (firstType.label < secondType.label) {
                            return -1;
                        }
                        if (firstType.label > secondType.label) {
                            return 1;
                        }
                        return 0;
                    });

                this.fields = data[1]!.map((field: any) => {
                    return {
                        label: field.Name,
                        value: field.FieldName,
                    };
                });
                this.fieldOptions =
                    this.fields?.filter((f) => f.value !== this.fieldName) ||
                    [];
                this.filterOption();
                // add patchEditValues fn to end of execution stack
                // so select2 loads options first
                setTimeout(() => {
                    this.patchEditValues(JSON.parse(JSON.stringify(data[1])));
                }, 0);
            }),

            this.store
                .pipe(
                    select(getApplicationRoles),
                    filter(
                        (roles: any) => !!roles[this.route.getApplicationKey()]
                    ),
                    map((roles: any) => roles[this.route.getApplicationKey()])
                )
                .subscribe((roles: any) => {
                    this.applicationRoles = roles.map((role: any) => {
                        return {
                            label: role.Name,
                            value: role.Key,
                        };
                    });
                    this.applicationRoles?.unshift({
                        label: 'App Admins',
                        value: 'admins',
                    });
                }),

            // FieldType listener
            this.form.controls.FieldType.valueChanges.subscribe(
                (type: string) => {
                    if (this.valueControl.length > 0) return;
                    if (type?.toLowerCase().includes('multiple'))
                        this.addValue();
                    else {
                        this.removeAllValue();
                    }

                    if (type == 'Text') {
                        this.form.controls.FieldSize.addValidators([
                            Validators.required,
                        ]);
                        this.form.controls.FieldSize.updateValueAndValidity();
                    } else {
                        this.form.controls.FieldSize.removeValidators([
                            Validators.required,
                        ]);
                        this.form.controls.FieldSize.updateValueAndValidity();
                    }
                }
            )
        );
    };

    /**
     * Close modal
     */
    close = () => {
        this.modal.dismissAll();
        this.store.dispatch(new LoadFields(this.route.getTableKey()));
    };

    /**
     * Add a input field in case of enums
     */
    addValue = (
        value: string | null = null,
        FieldKey?: string | null,
        isActive: boolean = true
    ) => {
        if (this.valueControl.controls.length >= 100) {
            this.alert.toast(
                ToastTypes.ERROR,
                'Error',
                'Maximum 100 options allowed.'
            );
            return;
        }
        this.valueControl.push(
            this.fb.group({
                IsActive: [isActive],
                value: [value, Validators.required],
                FieldKey: [FieldKey != null ? FieldKey : this.key],
            })
        );
    };

    /**
     * Remove all values from values array
     */
    removeAllValue = () => {
        let len = (this.form.controls.Values as UntypedFormArray).length;
        while (len) {
            this.removeValue(--len);
        }
    };

    /**
     * Remove value field
     */
    removeValue = (index: number) => {
        this.valueControl.removeAt(index);
    };

    /**
     * @returns true if enum is selected as field type
     */
    isENUMSelected = () =>
        this.form.controls.FieldType.value?.toLowerCase().includes('multiple');

    /**
     * @returns true if relation is selected as field type
     */
    isRelationSelected = () => {
        if (this.fieldName?.toLowerCase().includes('relat'))
            this.form.controls.FieldType.setValidators(null);
        return this.fieldName?.toLowerCase().includes('relat');
    };

    isUuidSelected = () => (this.selectedFieldObject as any)?.value2 === 'UUID';

    isLookupField = () => {
        if (this.form.value.LookupTargetFieldKey)
            this.form.controls.FieldType.setValidators(null);
        return this.form.value.LookupTargetFieldKey;
    };

    /**
     * @returns true if formula is selected
     */
    isFormulaSelected = () =>
        this.selectedFieldObject.label?.toLowerCase().includes('formula');

    /**
     * @returns true if field type is url
     */
    isUrl = () =>
        this.form.controls.FieldType.value === 'Formula - URL' ||
        this.form.controls.FieldType.value === 'URL';

    /**
     * @returns true if PII is selected
     */
    isPIISelected = () =>
        this.form.controls.FieldType?.value
            ?.toLowerCase()
            .includes('encrypted');

    /**
     * @returns true if File is selected
     */
    isFileSelected = () =>
        this.form.value.FieldType?.toLowerCase().includes('file');

    /**
     * @returns true if numeric is selected
     */
    isNumericOrCurrencySelected = () =>
        this.selectedFieldObject.label?.toLocaleLowerCase() == 'numeric' ||
        this.selectedFieldObject.label?.toLocaleLowerCase() == 'currency' ||
        this.selectedFieldObject.label?.toLocaleLowerCase() ==
        'formula - numeric' ||
        this.selectedFieldObject.label?.toLocaleLowerCase() ==
        'formula - currency';

    /**
     * @returns true if unique is not allowed for the field type
     */
    isUniqueNotAllowed = () => {
        if (this.form.value.FieldType == null) return true;
        let fields = [
            'Text - Multi-line',
            'Text - Multiple Choice',
            'Text - Multiple Select',
            'Checkbox',
            'File Attachment',
            'URL',
            'Formula - URL',
            'Encrypted (Extra Secure)',
        ].find((mode) => mode === this.form.value.FieldType);
        return fields;
    };

    /**
     * @returns true if nullable is not allowed for the field type
     */
    isNullableNotAllowed = () => {
        if (this.form.value.FieldType == null) return true;
        let fields = [
            'Text',
            'Text - Multi-line',
            'Text - Multiple Choice',
            'Text - Multiple Select',
            // 'Text - Variable Length',
            'Numeric',
            'Currency',
            'Datetime',
            'Date',
            'Phone Number',
            'Checkbox',
            'File Attachment',
            'Formula - Text',
            'Formula - Currency',
            'Formula - Date',
            'URL',
            'Formula - Numeric',
            'User',
            'Formula - URL',
            'Encrypted (Extra Secure)',
            'Email',
        ].find((mode) => mode === this.form.value.FieldType);
        return fields;
    };

    private registerError(line: number, column: number, errorMessage: string) {
        let modelMarkers: Array<any> = [];
        let decorations: Array<any> = [];
        modelMarkers.push({
            startLineNumber: line,
            startColumn: column - 1,
            endLineNumber: line,
            endColumn: column,
            message: errorMessage,
            severity: (window as any).monaco.MarkerSeverity.Error,
        });
        decorations.push({
            range: new (window as any).monaco.Range(line, column, line, column),
            options: {
                isWholeLine: true,
                glyphMarginClassName: 'errorIcon',
            },
        });

        this.oldFieldDecorations = this.editor.deltaDecorations(
            this.oldFieldDecorations,
            decorations
        );
        (window as any).monaco.editor.setModelMarkers(
            (window as any).monaco.editor.getModels()[0],
            'fields',
            modelMarkers
        );
    }

    /**
     * Control of value
     */
    get valueControl(): UntypedFormArray {
        return this.form.controls.Values as UntypedFormArray;
    }

    /**
     * Value form group
     */
    valueFormGroup = (value: any): UntypedFormGroup => value as UntypedFormGroup;

    /**
     * Field type select event listener
     */
    fieldSelectReceiver = (event: Array<Select2Option>) => {
        if (!event) return;
        this.selectedFieldObject = event[0];
        if (!this.isFormulaSelected()) {
            this.form.patchValue(
                {
                    Formula: '',
                    EvalFormula: null,
                    IsFormula: false,
                    FormulaFields: null,
                },
                { emitEvent: false }
            );
            this.setClearFormulaValidators(false);
        } else {
            this.form.controls.IsFormula.setValue(true);
            this.setClearFormulaValidators(true);
        }

        this.form.controls.IsHtmlAllowed.setValue(false);
        this.form.controls.IsFlyAdditionAllowed.setValue(false);
        this.form.controls.IsAlphaSort.setValue(false);

        if (!this.isENUMSelected()) {
            (this.form.controls.Values as UntypedFormArray).clear();
        }

        if ((this.selectedFieldObject as any)?.value2 === 'UUID') {
            this.form.patchValue({ IsUnique: true, IsNullable: true }, { emitEvent: false });
        }
    };

    /**
     * Set/Clear formula validators
     * @param set true if set validators else false
     */
    setClearFormulaValidators = (set: boolean) => {
        const formulaFields = ['Formula', 'FormulaFields'];
        formulaFields.forEach((field) => {
            if (set)
                this.form.controls[field].setValidators([Validators.required]);
            else this.form.controls[field].clearValidators();
            this.form.controls[field].updateValueAndValidity();
        });
    };

    filterOption(val?: any) {
        if (val)
            this.filterdOptions = this.fieldOptions?.filter((item: any) =>
                item.value
                    .toLowerCase()
                    .includes(val.target.value.toLowerCase())
            );
        else this.filterdOptions = this.fieldOptions;
    }

    rowDrop(event: any) {
        const currentIndex = event.currentIndex;
        const previousIndex = event.previousIndex;
        const control = this.valueControl.at(previousIndex);
        this.valueControl.controls.splice(previousIndex, 1);
        this.valueControl.controls.splice(currentIndex, 0, control);
    }

    getHTMLTags() {
        return `Allowed HTML Tags that can be included are:\n<${allowedHtmlTags.join(
            '>, <'
        )}>`;
    }
}
